import { X, MapPin, Video } from "lucide-react";
import {
  ClinicDoctorListingItem,
  DoctorAppointmentSettingItem,
} from "./types";
import { useState } from "react";
import placeholderAvatar from "../../assets/images/default_avatar.jpg";

interface ClinicSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  doctor: ClinicDoctorListingItem;
  settings: DoctorAppointmentSettingItem[];
  onConfirm: (setting: DoctorAppointmentSettingItem) => void;
}

export function ClinicSelectionModal({
  isOpen,
  onClose,
  doctor,
  settings,
  onConfirm,
}: ClinicSelectionModalProps) {
  const [selectedSetting, setSelectedSetting] =
    useState<DoctorAppointmentSettingItem | null>(null);

  if (!isOpen) return null;

  // Separate clinic and online settings
  // Clinic settings (clinicId starts with "SCL-" or doesn't start with "ONL-")
  // Online settings (clinicId starts with "ONL-")
  const clinicSettings = settings.filter(
    (s) => !s.clinicId.startsWith("ONL-")
  );
  const onlineSettings = settings.filter(
    (s) => s.clinicId.startsWith("ONL-")
  );

  // Clinic details first, then online (as per requirement)
  const orderedSettings = [...clinicSettings, ...onlineSettings];

  const isOnlineSetting = (setting: DoctorAppointmentSettingItem) =>
    setting.clinicId.startsWith("ONL-");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                <img
                  src={doctor.profileImage || placeholderAvatar}
                  alt={doctor.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = placeholderAvatar;
                  }}
                />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  Dr. {doctor.name}
                </h3>
                <p className="text-xs text-gray-500">
                  {doctor.specialization?.join(", ")}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-3">
          <p className="text-sm font-medium text-gray-700">
            How would you like to book this appointment?
          </p>

          {orderedSettings.map((setting) => {
            const isOnline = isOnlineSetting(setting);
            const isSelected = selectedSetting?.id === setting.id;

            return (
              <button
                key={setting.id}
                onClick={() => setSelectedSetting(setting)}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-start gap-3 ${
                  isSelected
                    ? "border-emerald-500 bg-emerald-50/50 shadow-sm"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                }`}
              >
                <div
                  className={`mt-0.5 p-2 rounded-lg flex-shrink-0 ${
                    isOnline
                      ? "bg-blue-100 text-blue-600"
                      : "bg-emerald-100 text-emerald-600"
                  }`}
                >
                  {isOnline ? (
                    <Video className="w-4 h-4" />
                  ) : (
                    <MapPin className="w-4 h-4" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900 text-sm">
                      {isOnline ? "Online Consultation" : setting.clinicName}
                    </h4>
                    <span className="text-emerald-700 font-bold text-sm whitespace-nowrap ml-2">
                      ₹{setting.fees}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {isOnline
                      ? "Video/audio call, patient joins remotely"
                      : `In-person at this clinic`}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {selectedSetting && (
              <>
                Selected:{" "}
                <span className="font-semibold text-gray-700">
                  {isOnlineSetting(selectedSetting)
                    ? "Online Consultation"
                    : selectedSetting.clinicName}
                </span>
              </>
            )}
          </div>
          <button
            onClick={() => {
              if (selectedSetting) onConfirm(selectedSetting);
            }}
            disabled={!selectedSetting}
            className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed shadow-sm"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
